/**
 * Gamification System
 * Points, badges, and achievements for community engagement
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Trophy,
  Star,
  Shield,
  Radio,
  MapPin,
  Users,
  Zap,
  Award,
  Target,
  Eye,
  Heart,
  MessageSquare,
  TrendingUp,
  Lock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Badge definitions
const BADGES = {
  // Onboarding
  first_login: {
    id: 'first_login',
    name: 'Welcome Aboard',
    description: 'Complete your first login',
    icon: Star,
    color: 'text-amber-400',
    points: 10,
    tier: 'bronze'
  },
  profile_complete: {
    id: 'profile_complete',
    name: 'Identity Verified',
    description: 'Complete your profile',
    icon: Shield,
    color: 'text-teal-400',
    points: 25,
    tier: 'bronze'
  },
  
  // Scanner activity
  first_scanner: {
    id: 'first_scanner',
    name: 'First Responder',
    description: 'Listen to your first scanner',
    icon: Radio,
    color: 'text-green-400',
    points: 15,
    tier: 'bronze'
  },
  scanner_10: {
    id: 'scanner_10',
    name: 'Scanner Enthusiast',
    description: 'Listen to 10 different scanners',
    icon: Radio,
    color: 'text-blue-400',
    points: 50,
    tier: 'silver'
  },
  scanner_50: {
    id: 'scanner_50',
    name: 'Scanner Pro',
    description: 'Listen to 50 different scanners',
    icon: Radio,
    color: 'text-purple-400',
    points: 150,
    tier: 'gold'
  },
  
  // Safety checks
  first_safety_check: {
    id: 'first_safety_check',
    name: 'Safety First',
    description: 'Check your first safety score',
    icon: MapPin,
    color: 'text-teal-400',
    points: 20,
    tier: 'bronze'
  },
  route_planner: {
    id: 'route_planner',
    name: 'Route Planner',
    description: 'Plan 5 safe routes',
    icon: Target,
    color: 'text-amber-400',
    points: 75,
    tier: 'silver'
  },
  
  // Community engagement
  first_report: {
    id: 'first_report',
    name: 'Community Guardian',
    description: 'Submit your first community report',
    icon: Users,
    color: 'text-blue-400',
    points: 30,
    tier: 'bronze'
  },
  reporter_10: {
    id: 'reporter_10',
    name: 'Neighborhood Watch',
    description: 'Submit 10 community reports',
    icon: Eye,
    color: 'text-purple-400',
    points: 100,
    tier: 'silver'
  },
  helpful_voter: {
    id: 'helpful_voter',
    name: 'Helpful Voter',
    description: 'Vote on 25 community reports',
    icon: MessageSquare,
    color: 'text-green-400',
    points: 50,
    tier: 'bronze'
  },
  
  // Safety check-ins
  first_checkin: {
    id: 'first_checkin',
    name: 'Safety Conscious',
    description: 'Send your first "I\'m Safe" check-in',
    icon: Heart,
    color: 'text-red-400',
    points: 25,
    tier: 'bronze'
  },
  checkin_streak_7: {
    id: 'checkin_streak_7',
    name: 'Week Warrior',
    description: '7-day check-in streak',
    icon: Zap,
    color: 'text-amber-400',
    points: 100,
    tier: 'silver'
  },
  
  // Premium badges
  early_adopter: {
    id: 'early_adopter',
    name: 'Early Adopter',
    description: 'Join VeriWatch in the first month',
    icon: Trophy,
    color: 'text-amber-500',
    points: 500,
    tier: 'gold'
  },
  founder: {
    id: 'founder',
    name: 'Founding Member',
    description: 'Purchase Lifetime Founder tier',
    icon: Award,
    color: 'text-yellow-400',
    points: 1000,
    tier: 'platinum'
  }
};

// Tier colors
const TIER_COLORS = {
  bronze: 'from-amber-700 to-amber-900',
  silver: 'from-slate-400 to-slate-600',
  gold: 'from-yellow-400 to-amber-600',
  platinum: 'from-purple-400 to-indigo-600'
};

// Level thresholds
const LEVELS = [
  { level: 1, name: 'Rookie', minPoints: 0 },
  { level: 2, name: 'Watcher', minPoints: 100 },
  { level: 3, name: 'Guardian', minPoints: 300 },
  { level: 4, name: 'Sentinel', minPoints: 600 },
  { level: 5, name: 'Protector', minPoints: 1000 },
  { level: 6, name: 'Defender', minPoints: 1500 },
  { level: 7, name: 'Champion', minPoints: 2500 },
  { level: 8, name: 'Hero', minPoints: 4000 },
  { level: 9, name: 'Legend', minPoints: 6000 },
  { level: 10, name: 'VeriWatch Elite', minPoints: 10000 }
];

const BadgeCard = ({ badge, earned = false, earnedAt = null }) => {
  const Icon = badge.icon;
  const tierGradient = TIER_COLORS[badge.tier];
  
  return (
    <div className={`p-3 rounded-lg border ${
      earned 
        ? 'border-teal-500/50 bg-teal-500/10' 
        : 'border-slate-700 bg-slate-800/30 opacity-50'
    }`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tierGradient} flex items-center justify-center`}>
          {earned ? (
            <Icon className="w-5 h-5 text-white" />
          ) : (
            <Lock className="w-4 h-4 text-white/50" />
          )}
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-white">{badge.name}</h4>
          <p className="text-xs text-slate-400 mt-0.5">{badge.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-400">
              +{badge.points} pts
            </Badge>
            <Badge variant="outline" className={`text-[10px] capitalize`}>
              {badge.tier}
            </Badge>
          </div>
          {earned && earnedAt && (
            <p className="text-[10px] text-slate-500 mt-1">
              Earned {new Date(earnedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const LevelProgress = ({ points, level, nextLevel }) => {
  const progress = nextLevel 
    ? ((points - level.minPoints) / (nextLevel.minPoints - level.minPoints)) * 100
    : 100;
  
  return (
    <div className="p-4 rounded-lg bg-gradient-to-br from-teal-600/20 to-slate-800 border border-teal-500/30">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center">
            <span className="text-xl font-bold text-teal-400">{level.level}</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">{level.name}</h3>
            <p className="text-sm text-teal-400">{points.toLocaleString()} points</p>
          </div>
        </div>
        <Trophy className="w-8 h-8 text-amber-400" />
      </div>
      
      {nextLevel && (
        <div>
          <div className="flex justify-between text-xs text-slate-400 mb-1">
            <span>Progress to {nextLevel.name}</span>
            <span>{nextLevel.minPoints - points} pts to go</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      )}
    </div>
  );
};

// Main Gamification Component
export const GamificationPanel = ({ isOpen, onClose }) => {
  const { token, user } = useAuth();
  const [userStats, setUserStats] = useState({
    points: 185, // Demo data
    earnedBadges: ['first_login', 'first_scanner', 'first_safety_check'],
    scannersListened: 7,
    reportsSubmitted: 2,
    routesPlanned: 3,
    checkinsCompleted: 5
  });

  // Calculate level
  const currentLevel = LEVELS.reduce((acc, level) => 
    userStats.points >= level.minPoints ? level : acc, LEVELS[0]);
  const nextLevel = LEVELS.find(l => l.minPoints > userStats.points);

  // Sort badges by earned status
  const sortedBadges = Object.values(BADGES).sort((a, b) => {
    const aEarned = userStats.earnedBadges.includes(a.id);
    const bEarned = userStats.earnedBadges.includes(b.id);
    if (aEarned && !bEarned) return -1;
    if (!aEarned && bEarned) return 1;
    return 0;
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-card border-slate-800 max-w-lg max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Trophy className="w-5 h-5 text-amber-400" />
            Your Achievements
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(85vh-100px)]">
          <div className="space-y-6 py-4">
            {/* Level Progress */}
            <LevelProgress 
              points={userStats.points}
              level={currentLevel}
              nextLevel={nextLevel}
            />

            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-2">
              {[
                { label: 'Scanners', value: userStats.scannersListened, icon: Radio },
                { label: 'Reports', value: userStats.reportsSubmitted, icon: Users },
                { label: 'Routes', value: userStats.routesPlanned, icon: Target },
                { label: 'Check-ins', value: userStats.checkinsCompleted, icon: Heart }
              ].map(stat => (
                <div key={stat.label} className="text-center p-2 rounded-lg bg-slate-800/50">
                  <stat.icon className="w-4 h-4 mx-auto mb-1 text-teal-400" />
                  <p className="text-lg font-bold text-white">{stat.value}</p>
                  <p className="text-[10px] text-slate-400">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Badges */}
            <div>
              <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-400" />
                Badges ({userStats.earnedBadges.length}/{Object.keys(BADGES).length})
              </h3>
              <div className="grid grid-cols-1 gap-2">
                {sortedBadges.map(badge => (
                  <BadgeCard
                    key={badge.id}
                    badge={badge}
                    earned={userStats.earnedBadges.includes(badge.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// Trophy button for header
export const GamificationButton = ({ onClick }) => {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      className="text-amber-400 hover:text-amber-300 hover:bg-slate-800 relative"
      title="Achievements"
    >
      <Trophy className="w-4 h-4" />
    </Button>
  );
};

export { BADGES, LEVELS };
export default GamificationPanel;
